import React, { useCallback } from 'react';
import {
  Header as HeaderComponent,
  Button,
  FlagOutline,
  DropdownMenu,
  ChevronDown,
  LogoutOutline,
  DropdownMenuContainer,
  DropdownMenuButton,
} from 'ui';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import CXLogo from 'assets/corenetx-logo.png';
import { useTrainingEnvAuth } from '../../auth/TrainingEnvAuthProvider';
import { useFeatureFlags } from 'feature-flags/useFeatureFlags';

const { MenuContainer, Link } = HeaderComponent;

interface IPreLoginHeader {
  showLoginButton?: boolean;
}

export const PreLoginHeaderTrainingEnv: React.FC<IPreLoginHeader> = ({ showLoginButton = true, ...props }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const redirect = useCallback((to: string) => {
    navigate(to);
  }, []);
  const { enableToggleFeatureFlagPanel } = useFeatureFlags();

  const { logout, trainingUser } = useTrainingEnvAuth();
  const showMenu = showLoginButton;

  const trainingUserName = trainingUser?.name;

  return (
    <HeaderComponent
      {...props}
      path={pathname}
      redirect={redirect}
      logo={
        <RouterLink tabIndex={-1} to="/" data-cy="training-env-header-logo">
          <div className="flex">
            <button className="focus:outline-none focus-within:ring-2 ring-primary-60 ring-offset-4">
              <img alt="Corenet X logo" src={CXLogo} className="w-[125px]" />
            </button>
            <button className="focus:outline-none focus-within:ring-2 ring-primary-60 ring-offset-4">
              <div>{'\u00A0 |  Training environment'}</div>
            </button>
          </div>
        </RouterLink>
      }
    >
      {showMenu ? (
        <MenuContainer>
          {enableToggleFeatureFlagPanel && (
            <Link path="/toggle-flags">
              <div className="flex gap-x-2 items-center">
                <FlagOutline className="flex-shrink-0 w-6 h-6" />
                <span>Feature Flags</span>
              </div>
            </Link>
          )}
          {showLoginButton && (
            <div className="w-max">
              <div className="hidden sm:block">
                {trainingUserName ? (
                  <div className="group relative">
                    <DropdownMenu
                      controlNode={
                        <div className="flex justify-center items-center flex-shrink-0 w-auto min-w-[2.75rem] px-3 h-11 rounded-full group-focus-within:bg-gray-10 hover:bg-gray-10 border border-gray-300">
                          {`${trainingUserName}`}
                          <ChevronDown className="text-gray-70 flex-shrink-0 w-6 h-6 stroke-[1.25]" />
                        </div>
                      }
                      align="right"
                      data-cy="pre-login-header_training-profile-name"
                    >
                      <div className="mt-6 w-64">
                        <DropdownMenuContainer>
                          <DropdownMenuButton
                            label={
                              <div className="flex space-x-2">
                                <LogoutOutline className="text-gray-70 flex-shrink-0 w-6 h-6 stroke-[1.25] mr-2" />
                                {`Log out of training environment`}
                              </div>
                            }
                            onClick={() => logout()}
                            data-cy="pre-login-header_training-logout-button"
                          />
                        </DropdownMenuContainer>
                      </div>
                    </DropdownMenu>
                  </div>
                ) : (
                  <Button
                    data-cy="pre-login-header_training-login-button"
                    label={'Login'}
                    onClick={() => navigate('/login-with-otp')}
                  />
                )}
              </div>
              <div className="sm:hidden">
                {trainingUserName ? (
                  <div className="flex space-x-2" onClick={() => logout()}>
                    <LogoutOutline className="text-gray-70 flex-shrink-0 w-6 h-6 stroke-[1.25]" />
                    {`Log out of training environment`}
                  </div>
                ) : (
                  <Link path="/login-with-otp">{'Login'}</Link>
                )}
              </div>
            </div>
          )}
        </MenuContainer>
      ) : null}
    </HeaderComponent>
  );
};
